import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";

function fmt(v: number): string {
  const abs = Math.abs(v);
  const prefix = v < 0 ? "-$" : "$";
  if (abs >= 1000) return `${prefix}${(abs / 1000).toFixed(1)}k`;
  return `${prefix}${abs.toFixed(0)}`;
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground p-4">
      {label}
    </div>
  );
}

export default function SessionInsightWidget() {
  const { analyticsData, isDark, t } = useDashboardData();

  if (!analyticsData) {
    return (
      <Card className="h-full">
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  const { insight, hasEnoughData } = analyticsData.sessionStats;

  const moodLabel = (mood: string): string => {
    const map: Record<string, string> = {
      excellent: t.analytics.sessionMoodExcellent,
      good: t.analytics.sessionMoodGood,
      neutral: t.analytics.sessionMoodNeutral,
      poor: t.analytics.sessionMoodPoor,
      bad: t.analytics.sessionMoodBad,
    };
    return map[mood] ?? mood;
  };

  const hasInsight = hasEnoughData && insight.type !== "none";

  if (!hasInsight) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader
          label={t.analytics.sessionsLabel ?? "Sessions"}
          title={t.analytics.sessionInsightLabel}
          isDark={isDark}
          inline
        />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.notEnoughSessionData ?? t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  let insightText = "";
  if (insight.type === "timeBucket") {
    insightText = t.analytics.sessionInsightTimeBucket
      .replace("{pct}", String(insight.pct ?? 0))
      .replace("{bucket}", insight.bucketLabel ?? "");
  } else if (insight.type === "mood") {
    insightText = t.analytics.sessionInsightMood
      .replace("{mood}", moodLabel(insight.mood ?? ""))
      .replace("{avg}", fmt(insight.avgPnl ?? 0));
  } else if (insight.type === "duration") {
    insightText = t.analytics.sessionInsightDuration
      .replace("{dur}", insight.durationLabel ?? "")
      .replace("{avg}", fmt(insight.avgPnl ?? 0));
  } else {
    // no-op: insightText stays "" for unrecognized types
  }

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.analytics.sessionsLabel ?? "Sessions"}
        title={t.analytics.sessionInsightLabel}
        isDark={isDark}
        inline
      />
      <CardContent className="flex-1 px-4 pb-4 pt-2">
        <div className={`rounded-lg px-4 py-3 border ${isDark ? "bg-muted/40 border-border" : "bg-blue-50 border-blue-200"}`}>
          <p className="text-sm">{insightText}</p>
        </div>
      </CardContent>
    </Card>
  );
}
